import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import GoogleAnalyticsSetUserId from 'components/atoms/GoogleAnalyticsSetUserId';
import NetworkToRedux from 'components/atoms/NetworkToRedux';
import PendoInitialize from 'components/atoms/PendoInitialize';
import Auth0TokenHandler from 'components/miscellaneous/Auth0TokenHandler';
import SessionTransferGate from 'components/miscellaneous/SessionTransferGate';
import Routes from 'routes';
import SiteUnderMaintenance from 'routes/PageNotFound/SiteUnderMaintenance';
import { WhiteLabelTitle } from 'types/models/white-label';
import getWhiteLabelConfig from 'utils/get-white-label-config';

const { VITE_AUTH0_DOMAIN, VITE_AUTH0_AUDIENCE, BASE_URL } =
  import.meta.env;

const { title, description, faviconName, auth0ClientId } =
  getWhiteLabelConfig();

const useFireGtmPageViewOnNavigation = () => {
  const location = useLocation();
  const isInitialPageView = useRef(true);
  /** Last page visited before the current page. */
  const entryPageRef = useRef({
    urlWithoutOrigin: document.location.pathname + document.location.search + document.location.hash,
    title: document.title
  });

  // Fire GTM pageview on client-side navigations.
  useEffect(() => {
    const currentUrl = location.pathname + location.search + location.hash;
    const currentTitle = document.title;

    if (isInitialPageView.current) {
      isInitialPageView.current = false;
      entryPageRef.current = { urlWithoutOrigin: currentUrl, title: currentTitle };
      // Initial pageview is fired in src/index.tsx, so we skip here.
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'pageview',
        entryUrlWithoutOrigin: entryPageRef.current.urlWithoutOrigin,
        entryPageTitle: entryPageRef.current.title
      }
    });
    entryPageRef.current = { urlWithoutOrigin: currentUrl, title: currentTitle };
  }, [location]);
};

function App(): JSX.Element {
  useFireGtmPageViewOnNavigation();

  // App initialisation logic.
  useEffectOnce(() => {
    // If this is a Minebot instance, add 'Minebot' class to the root element.
    // This will cause the correct styles to be used.
    if (title === WhiteLabelTitle.MINEBOT) {
      const rootNode = document.getElementById('farmbot');
      if (rootNode) {
        rootNode.className += `${rootNode.className ? ' ' : ''}Minebot`;
      }
    }
  });

  const { showMaintenancePage } = useFlags();

  const navigate = useNavigate();
  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? '/', { replace: true });
  };

  return (
    <HelmetProvider>
      <Auth0Provider
        domain={VITE_AUTH0_DOMAIN}
        clientId={auth0ClientId}
        useRefreshTokens
        useRefreshTokensFallback
        cacheLocation="localstorage"
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: VITE_AUTH0_AUDIENCE
        }}
        onRedirectCallback={onRedirectCallback}
      >
        <Helmet>
          <title>{`${title}`}</title>
          <meta name={`${title}`} content={`${description}`} />
          <link
            rel="icon"
            type="image/png"
            href={`${BASE_URL}${faviconName}`}
            sizes="16x16"
          />
        </Helmet>
        <NetworkToRedux />
        <SessionTransferGate>
          <Auth0TokenHandler>
            {showMaintenancePage ? <SiteUnderMaintenance /> : <Routes />}
          </Auth0TokenHandler>
        </SessionTransferGate>
        <GoogleAnalyticsSetUserId />
        <PendoInitialize />
      </Auth0Provider>
    </HelmetProvider>
  );
}

export default App;
